import {edgeTable,triTable} from 'three/addons/objects/MarchingCubes.js';
// Marching cubes (Lorensen & Cline 1987) over a box grid of voxels, with the
// lookup tables three.js ships. Every vertex also carries the rest coordinate
// of the grain it came from, so a solid texture (Perlin 1985) can paint layers
// and faces on any cut, dent or crumb.
const CORNERS=[[0,0,0],[1,0,0],[0,1,0],[1,1,0],[0,0,1],[1,0,1],[0,1,1],[1,1,1]];
// Edge e joins corners EDGES[e]; the order follows three.js MarchingCubes.
const EDGES=[[0,1],[1,3],[2,3],[0,2],[4,5],[5,7],[6,7],[4,6],[0,4],[1,5],[3,7],[2,6]];

export class VoxelSurface{
  constructor(nx,ny,nz,maxTriangles){
    this.nx=nx;this.ny=ny;this.nz=nz;this.density=new Float32Array(nx*ny*nz);
    this.positions=new Float32Array(maxTriangles*9);this.normals=new Float32Array(maxTriangles*9);this.rests=new Float32Array(maxTriangles*9);
    this.maxVertices=maxTriangles*3;this.count=0;this.overflow=false;
    this.edgePosition=new Float32Array(36);this.edgeNormal=new Float32Array(36);this.edgeRest=new Float32Array(36);
    this.min=[0,0,0];this.max=[0,0,0];
  }
  // A light blur rounds voxel corners while a one-cell gap stays open:
  // density = 0.4 * self + 0.1 * (six neighbours). Below the floor mirrors self.
  // Only the box around the sand is processed; everything else is empty.
  bounds(occupancy){
    const {nx,ny,nz}=this,lo=[nx,ny,nz],hi=[-1,-1,-1];
    for(let k=0,q=0;k<nz;k++)for(let j=0;j<ny;j++)for(let i=0;i<nx;i++,q++){
      if(!occupancy[q])continue;
      if(i<lo[0])lo[0]=i;if(i>hi[0])hi[0]=i;if(j<lo[1])lo[1]=j;if(j>hi[1])hi[1]=j;if(k<lo[2])lo[2]=k;if(k>hi[2])hi[2]=k;
    }
    const size=[nx,ny,nz];
    return {lo:lo.map(v=>Math.max(0,v-1)),hi:hi.map((v,a)=>Math.min(size[a]-1,v+1)),empty:hi[0]<0};
  }
  // A light blur rounds voxel corners while a one-cell gap stays open:
  // density = 0.4 * self + 0.1 * (six neighbours). Below the floor mirrors self.
  blur(occupancy,lo,hi){
    const {nx,ny,nz,density:d}=this,sy=nx,sz=nx*ny;d.fill(0);
    for(let k=lo[2];k<=hi[2];k++)for(let j=lo[1];j<=hi[1];j++)for(let i=lo[0];i<=hi[0];i++){
      const q=i+j*sy+k*sz,o=occupancy[q]?1:0;
      let s=(i>0&&occupancy[q-1]?1:0)+(i<nx-1&&occupancy[q+1]?1:0)+(j>0?(occupancy[q-sy]?1:0):o)+(j<ny-1&&occupancy[q+sy]?1:0)+(k>0&&occupancy[q-sz]?1:0)+(k<nz-1&&occupancy[q+sz]?1:0);
      d[q]=.4*o+.1*s;
    }
  }
  gradient(i,j,k,out,o){
    const {nx,ny,nz,density:d}=this,sy=nx,sz=nx*ny,q=i+j*sy+k*sz;
    out[o]=(i>0?d[q-1]:d[q])-(i<nx-1?d[q+1]:d[q]);
    out[o+1]=(j>0?d[q-sy]:d[q])-(j<ny-1?d[q+sy]:d[q]);
    out[o+2]=(k>0?d[q-sz]:d[q])-(k<nz-1?d[q+sz]:d[q]);
  }
  // occupancy: 0/1 per cell; rest: 3 floats per cell; origin: lower corner of cell (0,0,0).
  build(occupancy,rest,origin,cell,iso=.5){
    const box=this.bounds(occupancy);
    const min=this.min,max=this.max;min[0]=min[1]=min[2]=Infinity;max[0]=max[1]=max[2]=-Infinity;
    if(box.empty){this.count=0;this.overflow=false;return 0;}
    const {lo,hi}=box;this.blur(occupancy,lo,hi);
    const {nx,ny,nz,density:d,positions:P,normals:N,rests:R,edgePosition:ep,edgeNormal:en,edgeRest:er}=this,sy=nx,sz=nx*ny;
    const ga=[0,0,0],gb=[0,0,0];let v=0;this.overflow=false;
    const field=this.field??=new Float32Array(8);
    for(let k=lo[2];k<Math.min(nz-1,hi[2]+1);k++)for(let j=lo[1];j<Math.min(ny-1,hi[1]+1);j++)for(let i=lo[0];i<Math.min(nx-1,hi[0]+1);i++){
      const q=i+j*sy+k*sz;
      for(let c=0;c<8;c++)field[c]=d[q+CORNERS[c][0]+CORNERS[c][1]*sy+CORNERS[c][2]*sz];
      let cube=0;
      if(field[0]<iso)cube|=1;if(field[1]<iso)cube|=2;if(field[2]<iso)cube|=8;if(field[3]<iso)cube|=4;
      if(field[4]<iso)cube|=16;if(field[5]<iso)cube|=32;if(field[6]<iso)cube|=128;if(field[7]<iso)cube|=64;
      const bits=edgeTable[cube];if(!bits)continue;
      for(let e=0;e<12;e++){
        if(!(bits&(1<<e)))continue;
        const [a,b]=EDGES[e],ca=CORNERS[a],cb=CORNERS[b],fa=field[a],fb=field[b];
        const mu=Math.abs(fb-fa)<1e-6?.5:(iso-fa)/(fb-fa),o=e*3;
        for(let axis=0;axis<3;axis++)ep[o+axis]=origin[axis]+(([i,j,k][axis]+ca[axis]+.5)+(cb[axis]-ca[axis])*mu)*cell;
        this.gradient(i+ca[0],j+ca[1],k+ca[2],ga,0);this.gradient(i+cb[0],j+cb[1],k+cb[2],gb,0);
        for(let axis=0;axis<3;axis++)en[o+axis]=ga[axis]+(gb[axis]-ga[axis])*mu;
        // The vertex takes the colour coordinate of its inside grain.
        const inside=fa>=fb?q+ca[0]+ca[1]*sy+ca[2]*sz:q+cb[0]+cb[1]*sy+cb[2]*sz;
        er[o]=rest[inside*3];er[o+1]=rest[inside*3+1];er[o+2]=rest[inside*3+2];
      }
      const base=cube<<4;
      for(let t=0;triTable[base+t]!==-1;t+=3){
        if(v+3>this.maxVertices){this.overflow=true;break;}
        for(let s=0;s<3;s++){
          const o=triTable[base+t+s]*3,w=v*3;
          P[w]=ep[o];P[w+1]=ep[o+1];P[w+2]=ep[o+2];
          const l=Math.hypot(en[o],en[o+1],en[o+2])||1;N[w]=en[o]/l;N[w+1]=en[o+1]/l;N[w+2]=en[o+2]/l;
          R[w]=er[o];R[w+1]=er[o+1];R[w+2]=er[o+2];
          for(let axis=0;axis<3;axis++){if(P[w+axis]<min[axis])min[axis]=P[w+axis];if(P[w+axis]>max[axis])max[axis]=P[w+axis];}
          v++;
        }
      }
    }
    this.count=v;return v;
  }
}
